import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Observable } from "rxjs";
import { take } from "rxjs/operators";
import { AccessoryCategory, Constants, Positions, HTTPStatusCode } from "../core/Constants";
import { API_CMS_ENDPOINT, API_ENDPOINT, BRAND } from "../core/DI";
import { LastSubject } from "../core/LastSubject";
import { BoltsNutsInfos } from "../core/models/bolts-nuts-infos.model";
import { GenericSummaryCardDtoResponse } from "../core/models/generic-summary-card.dto-response";
import { MakeYearModel } from "../core/models/make-year-model.model";
import { OETireSizeFitmentDtoResponse, OETireSizeFitmentNote } from "../core/models/oe-tire-size-fitment.dto-response";
import { PackageBuilderPackage } from "../core/models/package-builder-package";
import { PackageBuilderSelections } from "../core/models/package-builder-selections.model";
import { PackageBuilderSummaryProduct } from "../core/models/package-builder-summary-product.model";
import { PackageBuilderSummary } from "../core/models/package-builder-summary.model";
import { PopupWarning } from "../core/models/popup-warning.model";
import { ProductIdsModel } from "../core/models/product-ids.model";
import { Product } from "../core/models/product.model";
import { StaggeredPackageBuilderSummary } from "../core/models/staggered-package-builder-summary.model";
import { StaggeredPackageBuilderModel } from "../core/models/staggered-package-builder.model";
import { VehicleOemDataDtoResponse } from "../core/models/vehicule-oem-data.dto-response";
import { Helper } from "../shared/helper";
import { MakeYearModelService } from "../shared/make-year-model.service";
import { ProductNotesService } from "../shared/notes/product-notes.service";
import { TireService } from "../shared/tire.service";
import { PackageBuilderFlow } from "./model/package-builder-flow.enum";
import { WheelService } from "app/shared/wheel.service";
import { KitComponentsDtoResponse } from "app/core/models/kit-components.dto-response";
import { ActivatedRoute } from "@angular/router";
import { ProductDetail } from "app/core/models/product-card-detail.dto-response";
import { TireSideWallDtoResponse } from 'app/core/models/tire-sidewall.dto-response';
import { SettingService } from '../shared/setting.service';
import { LeadTimeDtoResponse } from "../core/models/lead-time-dto-response";

export class SelectProductIDs {
  frontId = '';
  rearId = '';
  type = '';
}

@Injectable({
  providedIn: 'root',
})
export class PackageBuilderService {
  private packageBuilderModel = new StaggeredPackageBuilderModel();
  private packageBuilderSummary = new StaggeredPackageBuilderSummary();
  private packageChangeSubject = new LastSubject<StaggeredPackageBuilderModel>();
  private packageSummaryChangeSubject = new LastSubject<StaggeredPackageBuilderSummary>();
  private packageBuilderFlowSubject = new BehaviorSubject<PackageBuilderFlow>(PackageBuilderFlow.NONE);
  private packageBuilderOEMChangeSubject = new LastSubject<VehicleOemDataDtoResponse>();
  private componentsSubject = new LastSubject<KitComponentsDtoResponse[]>();

  private packageBuilderFlow: PackageBuilderFlow = PackageBuilderFlow.NONE;
  private packageBuilderMym: MakeYearModel;
  private wheelStagger: string;
  private tireStagger: string;
  //private tireStaggerInitial: string; //Added to validate stagger value if additional filters applied after staggerfilter
  private packageBuilderOEMData: VehicleOemDataDtoResponse;
  private isReady = new LastSubject<boolean>();
  private wheelRemoved = new LastSubject<boolean>();
  private tireRemoved = new LastSubject<boolean>();
  private productAdded = new LastSubject<SelectProductIDs>();
  private nextTabSelected = new LastSubject<AccessoryCategory>();
  private updateNavigation = new LastSubject<boolean>();
  private addProductCard = new LastSubject<GenericSummaryCardDtoResponse>();
  private clearForNewSearch = new LastSubject<boolean>();

  private redirectedFromProductDetails = new LastSubject<boolean>();
  private frontHubRingRequired = false;
  private rearHubRingRequired = false;
  private wheelSeat = '';
  private tiresFirst: boolean;

  private apiEndpoint: string;
  private apiCmsEndpoint: string;
  public filterChangeSubject = new LastSubject<any>();
  private prodSelectorQtyChanged = new LastSubject<Product>();

  public wheelIsSteel = false;
  public wheelIsDirectFit = false;
  public wheelIsAggressive = false;
  public wheelBrand: string;
  public keysRequired = false;
  public wheelIsOpenEnded = false;
  public serviceSummaryCards = new Array<GenericSummaryCardDtoResponse>();
  public oeTireSizeFitsAggressive = false;
  public oeTireSizeFitmentNote: OETireSizeFitmentNote;
  public TPMSAvailable = false;
  public valvesRequired = false;
  public tireStuddingAvailable = false;

  public lastTabId: AccessoryCategory;
  public lastTabName: string;
  public id: string;
  public showSkip: boolean = false;
  public mountingAdded: boolean = false;
  public searchLogId: number = -1;
  public wheelDrillLeadTime: LeadTimeDtoResponse = null;

  // djb:loaded from session during i18n swicth
  public loadedFromSession = false;
  customParameter: {} = null;
  constructor(@Inject(API_ENDPOINT) apiEndpoint: string,
    @Inject(API_CMS_ENDPOINT) apiCmsEndpoint: string,
    @Inject(BRAND) private brand: string,
    private http: HttpClient,
    private translateService: TranslateService,
    private mayeYearModelService: MakeYearModelService,
    private productNotesService: ProductNotesService,
    private tireService: TireService,
    private wheelService: WheelService,
    private settingService: SettingService) {
    this.apiEndpoint = apiEndpoint;
    this.apiCmsEndpoint = apiCmsEndpoint;
    this.id = Helper.generateId(1);
    this.log('constructor');
    this.tiresFirst = Helper.isProductTypePreferenceTire();

    if (this.brand === Constants.BRAND_B2B) {
      this.settingService.getWheelDrillLeadTime().first().toPromise().then(res => {
        this.wheelDrillLeadTime = res;
      });
    }
  }

  get onProductSelectorQtyChanged(): Observable<Product> {
    return this.prodSelectorQtyChanged.asObservable();
  }

  get onFilterChange(): Observable<object> {
    return this.filterChangeSubject.asObservable();
  }

  get onPackageChange(): Observable<StaggeredPackageBuilderModel> {
    return this.packageChangeSubject.asObservable();
  }

  get onSummaryChange(): Observable<StaggeredPackageBuilderSummary> {
    return this.packageSummaryChangeSubject.asObservable();
  }

  get onFlowChange(): Observable<PackageBuilderFlow> {
    return this.packageBuilderFlowSubject.asObservable();
  }

  get onOEMChange(): Observable<VehicleOemDataDtoResponse> {
    return this.packageBuilderOEMChangeSubject.asObservable();
  }

  get onReadyChange(): Observable<boolean> {
    return this.isReady.asObservable();
  }

  get onAddProductCard(): Observable<GenericSummaryCardDtoResponse> {
    return this.addProductCard.asObservable();
  }

  get onProductAdded(): Observable<SelectProductIDs> {
    return this.productAdded.asObservable();
  }

  get onNextTabSelected(): Observable<AccessoryCategory> {
    this.log('pbs.OnNextTabSelected');
    return this.nextTabSelected.asObservable();
  }

  get onUpdateNavigation(): Observable<boolean> {
    return this.updateNavigation.asObservable();
  }

  get onComponents(): Observable<KitComponentsDtoResponse[]> {
    return this.componentsSubject.asObservable();
  }

  changeTab(catName: string, catId: AccessoryCategory) {
    this.log(`changeTab: ${catName} id: ${catId}`);
    this.nextTabSelected.next(catId);
    this.lastTabName = catName;
    this.lastTabId = catId;
    sessionStorage.setItem('SelectedTab', String(catId));
    // djb:hack-to save tab state when changed
    sessionStorage.setItem('lastTabName', catName);
    sessionStorage.setItem('lastTabId', catId.toString());
  }


  updateNav() {
    this.updateNavigation.next(true);
  }

  addProductSummaryCard(card: GenericSummaryCardDtoResponse) {
    this.addServiceCard(card);
    this.addProductCard.next(card);
  }

  private addServiceCard(serviceCard: GenericSummaryCardDtoResponse) {
    this.serviceSummaryCards.push(serviceCard);
  }

  //awp:cp added for new search to notify pb to clear itself.
  get onClearForNewSearch(): Observable<boolean> {
    return this.clearForNewSearch.asObservable();
  }

  forceClearForNewSearch() {
    this.clearForNewSearch.next(true);
  }

  //awm:cp added to bubble event up to packagebuilder component
  //to notify it that the wheel was removed
  get onWheelRemoved(): Observable<boolean> {
    return this.wheelRemoved.asObservable();
  }

  get onTireRemoved(): Observable<boolean> {
    return this.tireRemoved.asObservable();
  }

  // Used when we use the Add Tires button to transform the search-by-vehicle by wheels into the package builder.
  get onRedirectedFromProductDetails(): Observable<boolean> {
    return this.redirectedFromProductDetails.asObservable();
  }

  setRedirectedFromProductDetails(redirected: boolean): void {
    this.redirectedFromProductDetails.next(redirected);
  }

  setIsReady(ready: boolean): void {
    this.isReady.next(ready);
  }
  IsWheelAndTireSelected(): boolean {
    return (this.packageBuilderModel.front.wheel && this.packageBuilderModel.front.tire) ? true : false;
  }
  freeWMB(position: Positions): boolean {
    if (position === Positions.REAR)
      return this.packageBuilderModel.rear.tire && this.packageBuilderModel.rear.tire.freeWMB;
    return this.packageBuilderModel.front.tire && this.packageBuilderModel.front.tire.freeWMB;
  }
  getWheelIds(): ProductIdsModel {
    const ids = new ProductIdsModel();
    if (this.packageBuilderModel.front.wheel) {
      ids.front = this.packageBuilderModel.front.wheel.id;
      ids.frontQty = this.packageBuilderModel.front.wheel.quantity;
    }
    else {
      ids.front = '';
      ids.frontQty = 0;
    }
    if (this.packageBuilderModel.rear.wheel) {
      ids.rear = this.packageBuilderModel.rear.wheel.id;
      ids.rearQty = this.packageBuilderModel.rear.wheel.quantity;
    }
    else {
      ids.rear = '';
      ids.rearQty = 0;
    }
    return ids;
  }

  getTireIds(): ProductIdsModel {
    const ids = new ProductIdsModel();
    if (this.packageBuilderModel.front.tire) {
      ids.front = this.packageBuilderModel.front.tire.id;
      ids.frontQty = this.packageBuilderModel.front.tire.quantity;
    }
    else {
      ids.front = '';
      ids.frontQty = 0;
    }
    if (this.packageBuilderModel.rear.tire) {
      ids.rear = this.packageBuilderModel.rear.tire.id;
      ids.rearQty = this.packageBuilderModel.rear.tire.quantity;
    }
    else {
      ids.rear = '';
      ids.rearQty = 0;
    }
    return ids;
  }

  getFrontWheelOffset(): number {
    return this.packageBuilderSummary.front.wheel ? this.packageBuilderSummary.front.wheel.offset : null;
  }

  getRearWheelOffset(): number {
    return this.packageBuilderSummary.rear.wheel ? this.packageBuilderSummary.rear.wheel.offset : null;
  }

  addProduct(frontProduct: Product,
    frontProductSummary: PackageBuilderSummaryProduct,
    rearProduct: Product = null,
    rearProductSummary: PackageBuilderSummaryProduct = null,
    quickCheckout: boolean = false,
    type: string) {
    this.log('addProduct:' + type);
    switch (type) {
      case Constants.PRODUCT_TYPE_WHEEL:
        this.GetLeadTimeInternalMessage(frontProductSummary, frontProduct);

        if (rearProductSummary !== null)
          this.GetLeadTimeInternalMessage(rearProductSummary, rearProduct);

        this.setWheels(frontProduct, rearProduct, quickCheckout);
        this.setWheelSummaries(frontProductSummary, rearProductSummary);
        break;
      case Constants.PRODUCT_TYPE_TIRE:
        //const tirePressFront: TirePressureDtoResponse = new TirePressureDtoResponse();
        //const tirePressBack: TirePressureDtoResponse = new TirePressureDtoResponse();
        if (frontProduct != null) {
          if (frontProduct.warningNotes == null) {
            frontProduct.warningNotes = [];
          }
          //     else {
          //	tirePressFront.message = frontProduct.warningNotes[0];
          //	tirePressBack.message = frontProduct.warningNotes[1];
          //}
          if (frontProductSummary.warningNotes == null) {
            frontProductSummary.warningNotes = [];
          }

          //if (frontProduct.tirePressure == null) {
          //	frontProduct.tirePressure = [];
          //	frontProduct.tirePressure.push(tirePressFront);
          //	frontProduct.tirePressure.push(tirePressBack);
          //}
          //else {
          //  frontProduct.tirePressure.push(tirePressFront);
          //  frontProduct.tirePressure.push(tirePressBack);
          //}
        }
        if (frontProductSummary != null) {
          if (frontProductSummary.tirePressure == null) {
            frontProductSummary.tirePressure = [];

            //frontProductSummary.tirePressure.push(tirePressFront);
            //frontProductSummary.tirePressure.push(tirePressBack);
          }
          frontProductSummary.warningNotes = frontProductSummary.warningNotes.concat(frontProduct.warningNotes);
          //else {
          //  frontProductSummary.tirePressure.push(tirePressFront);
          //  frontProductSummary.tirePressure.push(tirePressBack);
          //}
        }
        if (frontProduct != null) {
          this.setTires(frontProduct, rearProduct, quickCheckout);
          this.setTireSummaries(frontProductSummary, rearProductSummary);
        }
        break;
    }
    const selectedProds = new SelectProductIDs();
    selectedProds.type = type;
    if (frontProduct != null) {
      selectedProds.frontId = frontProduct.id;
    }

    if (rearProduct != null) {
      selectedProds.rearId = rearProduct.id;
    }
    //awm:cp notify product was added so component is aware of change
    this.productAdded.next(selectedProds);
  }

  addTheLabours(products: Product[], position: Positions) {
    if (position === Positions.REAR) {
      if (this.packageBuilderModel.rear.labours == null || this.packageBuilderModel.rear.labours.length == 0)
        this.packageBuilderModel.rear.labours = products;
      else
        products.forEach(p => this.packageBuilderModel.rear.labours.push(p));
    }
    else {
      if (this.packageBuilderModel.front.labours == null || this.packageBuilderModel.front.labours.length == 0)
        this.packageBuilderModel.front.labours = products;
      else
        products.forEach(p => this.packageBuilderModel.front.labours.push(p));
    }
  }

  addLabours(products: Product[], productSummaries: PackageBuilderSummaryProduct[], position: Positions) {
    if (position) {
      if (position === Positions.FRONT) {
        this.packageBuilderModel.front.labours = products;
        this.packageBuilderSummary.front.labours = productSummaries;
      } else if (position === Positions.REAR) {
        this.packageBuilderModel.rear.labours = products;
        this.packageBuilderSummary.rear.labours = productSummaries;
      }
    }
    this.packageChangeSubject.next(this.packageBuilderModel);
    this.packageSummaryChangeSubject.next(this.packageBuilderSummary);
  }

  setWheels(frontWheel: Product, rearWheel: Product = null, quickCheckout: boolean = false) {
    if (!quickCheckout) {
      //this.setPackageBuilderFlow(PackageBuilderFlow.TIRES);
      this.setPackageBuilderFlow(PackageBuilderFlow.HARDWARE);
    }
    this.packageBuilderModel.front.wheel = frontWheel;
    this.packageBuilderModel.front.labours = [];

    this.packageBuilderModel.rear.wheel = rearWheel;
    this.packageBuilderModel.rear.labours = [];

    if (!this.tiresFirst) {//WheelFirst
      this.packageBuilderModel.front.tire = null;
      this.packageBuilderModel.front.hardwares = [];

      this.packageBuilderModel.rear.tire = null;
      this.packageBuilderModel.rear.hardwares = [];
      this.tireStuddingAvailable = false;
    }
    this.packageChangeSubject.next(this.packageBuilderModel);
  }

  setWheelSummaries(frontWheel: PackageBuilderSummaryProduct, rearWheel: PackageBuilderSummaryProduct = null) {

    this.packageBuilderSummary.front.wheel = frontWheel;
    this.packageBuilderSummary.front.labours = [];

    this.packageBuilderSummary.rear.wheel = rearWheel;
    this.packageBuilderSummary.rear.labours = [];

    if (!this.tiresFirst) { //WheelFirst
      this.packageBuilderSummary.front.tire = null;
      this.packageBuilderSummary.front.hardwares = [];

      this.packageBuilderSummary.rear.tire = null;
      this.packageBuilderSummary.rear.hardwares = [];

      this.serviceSummaryCards = new Array<GenericSummaryCardDtoResponse>();
    }
    this.packageBuilderSummary['wheelChanged'] = 1
    this.packageSummaryChangeSubject.next(this.packageBuilderSummary);
    delete this.packageBuilderSummary['wheelChanged'];
    // TODO: Change next line to get notes from product (FCO-503)
    // Apres verification: seulement si une ou des roues sont machinees.
    // Il devrait y avoir un flag dans ce que Joyce retourne qui est "Machined Yes/No"
    // this.addNoteString('FC.Notes.RestockingFees');
  }

  clear(changeFlow: boolean = true) {
    this.log(`clear: changeFlow=${changeFlow}`);
    if (changeFlow) {
      if (!this.tiresFirst) {
        if (this.packageBuilderFlow === PackageBuilderFlow.WHEELS) {
          this.setPackageBuilderFlow(PackageBuilderFlow.NONE);
        } else {
          this.setPackageBuilderFlow(PackageBuilderFlow.WHEELS);
        }
      }
      else {
        if (this.packageBuilderFlow === PackageBuilderFlow.TIRES) {
          this.setPackageBuilderFlow(PackageBuilderFlow.NONE);
        } else {
          this.setPackageBuilderFlow(PackageBuilderFlow.TIRES);
        }
      }
    }
    this.packageBuilderModel = new StaggeredPackageBuilderModel();
    this.packageBuilderSummary = new StaggeredPackageBuilderSummary();
    this.serviceSummaryCards = new Array<GenericSummaryCardDtoResponse>();
    this.wheelIsDirectFit = false;
    this.wheelIsAggressive = false;
    this.keysRequired = false;
    this.frontHubRingRequired = false;
    this.rearHubRingRequired = false;
    this.wheelSeat = '';
    this.wheelIsSteel = false;
    this.wheelBrand = '';
    this.wheelIsOpenEnded = false;
    this.oeTireSizeFitsAggressive = false;
    this.TPMSAvailable = false;
    this.valvesRequired = false;
    this.tireStuddingAvailable = false;
    this.packageChangeSubject.next(this.packageBuilderModel);
    this.packageSummaryChangeSubject.next(this.packageBuilderSummary);
  }

  reset() {
    this.packageBuilderFlow = PackageBuilderFlow.NONE;
    this.clear();
    this.log(`reset`);
    //djb:chokepoint-determine which subscription is leaking incrementally and messing up package-builder
    //this.isReady.unsubscribe();
    //this.wheelRemoved.unsubscribe();
    //this.productAdded.unsubscribe();
    //this.nextTabSelected.unsubscribe();
    //this.updateNavigation.unsubscribe();
    //this.packageBuilderFlowSubject.unsubscribe();
    //this.isReady = new LastSubject<boolean>();
    //this.wheelRemoved = new LastSubject<boolean>();
    //this.tireRemoved = new LastSubject<boolean>();
    //this.productAdded = new LastSubject<SelectProductIDs>();
    //this.nextTabSelected = new LastSubject<AccessoryCategory>();
    //this.updateNavigation = new LastSubject<boolean>();
    //this.addProductCard = new LastSubject<GenericSummaryCardDtoResponse>();
    //this.packageBuilderFlowSubject = new BehaviorSubject<PackageBuilderFlow>(PackageBuilderFlow.NONE);
  }

  getPackageBuilderModel(): StaggeredPackageBuilderModel {
    return this.packageBuilderModel;
  }

  clearForTires() {
    this.log('clearForTires(remove installation and studding from front and rear)');
    if (this.packageBuilderModel.front != null) {
      this.clearForTiresProducts(this.packageBuilderModel.front.hardwares);
    }
    if (this.packageBuilderModel.rear != null) {
      this.clearForTiresProducts(this.packageBuilderModel.rear.hardwares);
    }
    if (this.packageBuilderSummary.front != null) {
      this.clearForTiresSummary(this.packageBuilderSummary.front.hardwares);
    }
    if (this.packageBuilderSummary.rear != null) {
      this.clearForTiresSummary(this.packageBuilderSummary.rear.hardwares);
    }
    // djb:hack-clean up service items in pbservice
    this.serviceSummaryCards = new Array<GenericSummaryCardDtoResponse>();
    this.tireStuddingAvailable = false;
    //this.updateNav();
    //setTimeout(() => { this.updateNav(); }, 1);
  }

  clearForTiresProducts(products: Product[]) {
    // djb-hack-only expect one but seem to have multiple instances
    products = products.filter(item => item.category !== AccessoryCategory.Mounting);
    products = products.filter(item => item.category !== AccessoryCategory.Studding);
  }

  clearForTiresSummary(products: PackageBuilderSummaryProduct[]) {
    // djb-hack-only expect one but seem to have multiple instances
    products = products.filter(item => item.accessoryType !== AccessoryCategory.Mounting);
    products = products.filter(item => item.accessoryType !== AccessoryCategory.Studding);
  }

  setTires(frontTire: Product, rearTire: Product = null, quickCheckout: boolean = false) {
    if (!quickCheckout) {
      if (!this.tiresFirst || this.tireStuddingAvailable === true)
        this.setPackageBuilderFlow(PackageBuilderFlow.HARDWARE);
      else
        this.navigateToWheels();
    }
    this.packageBuilderModel.front.tire = frontTire;
    this.packageBuilderModel.rear.tire = rearTire;
    if (this.tiresFirst) {
      this.packageBuilderModel.front.wheel = null;
      this.packageBuilderModel.front.hardwares = [];
      this.packageBuilderModel.front.labours = [];

      this.packageBuilderModel.rear.wheel = null;
      this.packageBuilderModel.rear.hardwares = [];
      this.packageBuilderModel.rear.labours = [];

      this.wheelIsDirectFit = false;
      this.wheelIsAggressive = false;
      this.keysRequired = false;
      this.frontHubRingRequired = false;
      this.rearHubRingRequired = false;
      this.wheelSeat = '';
      this.wheelIsSteel = false;
      this.wheelBrand = '';
      this.wheelIsOpenEnded = false;
      this.oeTireSizeFitsAggressive = false;
      this.TPMSAvailable = false;
      this.valvesRequired = false;
    }
    this.packageChangeSubject.next(this.packageBuilderModel);
  }

  setTireSummaries(frontTire: PackageBuilderSummaryProduct, rearTire: PackageBuilderSummaryProduct = null) {
    this.serviceSummaryCards = new Array<GenericSummaryCardDtoResponse>();
    this.packageBuilderSummary.front.tire = frontTire;
    this.packageBuilderSummary.rear.tire = rearTire;
    if (this.tiresFirst) {
      this.packageBuilderSummary.front.wheel = null;
      this.packageBuilderSummary.front.hardwares = [];
      this.packageBuilderSummary.front.labours = [];

      this.packageBuilderSummary.rear.wheel = null;
      this.packageBuilderSummary.rear.hardwares = [];
      this.packageBuilderSummary.rear.labours = [];
    }
    this.packageSummaryChangeSubject.next(this.packageBuilderSummary);
  }

  deselectWheels() {
    this.packageBuilderModel.front.wheel = null;
    this.packageBuilderModel.front.labours = [];

    this.packageBuilderModel.rear.wheel = null;
    this.packageBuilderModel.rear.labours = [];
    this.packageChangeSubject.next(this.packageBuilderModel);

    this.packageBuilderSummary.front.wheel = null;
    this.packageBuilderSummary.front.labours = [];

    this.packageBuilderSummary.rear.wheel = null;
    this.packageBuilderSummary.rear.labours = [];

    if (this.tiresFirst) {
      //Remove all the Hardwares except the Studding
      if (this.packageBuilderModel.front.hardwares && this.packageBuilderModel.front.hardwares.length > 0) {
        this.packageBuilderModel.front.hardwares = this.packageBuilderModel.front.hardwares.filter(h => h.category === AccessoryCategory.Studding);
        this.packageBuilderSummary.front.hardwares = this.packageBuilderSummary.front.hardwares.filter(h => h.accessoryType === AccessoryCategory.Studding);
        if (this.packageBuilderModel.rear.hardwares) {
          this.packageBuilderModel.rear.hardwares = this.packageBuilderModel.rear.hardwares.filter(h => h.category === AccessoryCategory.Studding);
          this.packageBuilderSummary.rear.hardwares = this.packageBuilderSummary.rear.hardwares.filter(h => h.accessoryType === AccessoryCategory.Studding);
        }
      }
      else {
        this.packageBuilderModel.front.hardwares = [];
        this.packageBuilderModel.rear.hardwares = [];
        this.packageBuilderSummary.front.hardwares = [];
        this.packageBuilderSummary.rear.hardwares = [];
      }
      let servicecard = this.serviceSummaryCards.filter(c => c.frontProduct.details.details.find(d => d.label === 'Type').accessoryType === AccessoryCategory.Studding);
      this.serviceSummaryCards = servicecard;
    }
    else {
      this.packageBuilderModel.front.hardwares = [];
      this.packageBuilderModel.rear.hardwares = [];
      this.packageBuilderSummary.front.hardwares = [];
      this.packageBuilderSummary.rear.hardwares = [];
      this.serviceSummaryCards = new Array<GenericSummaryCardDtoResponse>();
    }
    this.packageSummaryChangeSubject.next(this.packageBuilderSummary);

    this.wheelIsDirectFit = false;
    this.wheelIsAggressive = false;
    this.keysRequired = false;
    this.frontHubRingRequired = false;
    this.rearHubRingRequired = false;
    this.wheelSeat = '';
    this.wheelIsSteel = false;
    this.wheelBrand = '';
    this.wheelIsOpenEnded = false;
    this.oeTireSizeFitsAggressive = false;
    this.TPMSAvailable = false;
    this.valvesRequired = false;
  }

  deselectTires() {
    // Remove Installation
    this.removeInstallation();
    this.removeStudding();
    this.tireStuddingAvailable = false;
    this.packageBuilderModel.front.tire = null;
    this.packageBuilderModel.front.labours = [];
    this.packageBuilderModel.rear.tire = null;
    this.packageBuilderModel.rear.labours = [];
    this.packageChangeSubject.next(this.packageBuilderModel);
    this.packageBuilderSummary.front.tire = null;
    this.packageBuilderSummary.front.labours = [];
    this.packageBuilderSummary.rear.tire = null;
    this.packageBuilderSummary.rear.labours = [];
    this.packageSummaryChangeSubject.next(this.packageBuilderSummary);
    this.serviceSummaryCards = new Array<GenericSummaryCardDtoResponse>();
  }
  removeInstallation() {
    //Remove Installation
    this.packageBuilderModel.front.hardwares = this.packageBuilderModel.front.hardwares.filter(h => h.category !== AccessoryCategory.Mounting);
    this.packageBuilderSummary.front.hardwares = this.packageBuilderSummary.front.hardwares.filter(h => h.accessoryType !== AccessoryCategory.Mounting);
    if (this.packageBuilderModel.rear.tire) {
      this.packageBuilderModel.rear.hardwares = this.packageBuilderModel.rear.hardwares.filter(h => h.category !== AccessoryCategory.Mounting);
      this.packageBuilderSummary.rear.hardwares = this.packageBuilderSummary.rear.hardwares.filter(h => h.accessoryType !== AccessoryCategory.Mounting);
    }
  }
  removeStudding() {
    //Remove Studding
    this.packageBuilderModel.front.hardwares = this.packageBuilderModel.front.hardwares.filter(h => h.category !== AccessoryCategory.Studding);
    this.packageBuilderSummary.front.hardwares = this.packageBuilderSummary.front.hardwares.filter(h => h.accessoryType !== AccessoryCategory.Studding);
    if (this.packageBuilderModel.rear.tire) {
      this.packageBuilderModel.rear.hardwares = this.packageBuilderModel.rear.hardwares.filter(h => h.category !== AccessoryCategory.Studding);
      this.packageBuilderSummary.rear.hardwares = this.packageBuilderSummary.rear.hardwares.filter(h => h.accessoryType !== AccessoryCategory.Studding);
    }
  }
  sendMakeYearModel(mym: MakeYearModel, searchfrom: string) {
    this.log('SendMakeYearModel recent search');
    const api = `${this.apiCmsEndpoint}/cart/RecentSearch/${searchfrom}`;
    this.http.post(api, mym).toPromise().then(response => {
    }).catch((err: any) => {
    });
  }

  setMakeYearModel(mym: MakeYearModel, currentWheelStagger: string = null, currentTireStagger: string = null, defaultStagger: string = null) {
    this.log('setMakeYearModel-get oem data');
    this.packageBuilderMym = mym;
    this.wheelStagger = currentWheelStagger;
    this.tireStagger = currentTireStagger;
    //this.tireStaggerInitial = currentTireStagger;
    if (mym != null && mym.make != null) {
      this.mayeYearModelService.getCarOemData(mym).then(oemData => {
        this.packageBuilderOEMData = oemData;
        const oemDuplicate = oemData;
        //awm:cp emit change to oem data, so we can pick it up and adjust the nav in the packagebuilder component.
        this.packageBuilderOEMChangeSubject.next(oemDuplicate);
      });
    }
  }

  logSearch() {
    const api = `${this.apiEndpoint}/wheels/log/vehicle`;
    return this.http.post<number>(api, {
        baseFilter: this.packageBuilderMym
      },
      { observe: 'response', responseType: 'json' })
      .subscribe(res => {
        if (res.status === HTTPStatusCode.NO_CONTENT) {
          this.searchLogId = -1;
        } else {
          this.searchLogId = res.body;
        }
      },
        Helper.handleError);
  }

  getMakeYearModel(): MakeYearModel {
    return this.packageBuilderMym;
  }
  isVehicleDually(): Boolean {
    return this.packageBuilderMym && this.packageBuilderMym.model && this.packageBuilderMym.model.includes('DRW');
  }
  setWheelStagger(currentWheelStagger: string = null) {
    this.wheelStagger = currentWheelStagger;
  }

  setTireStagger(currentTireStagger: string = null) {
    this.tireStagger = currentTireStagger;
  }

  //setTireStaggerInitial(currentTireStagger: string = null) {
  //  this.tireStaggerInitial = currentTireStagger;
  //}
  
  setTPMSAvailable(value: boolean) {
    this.TPMSAvailable = value;
  }
  setWheelSeat(seat: string = null) {
    this.wheelSeat = seat;
  }
  productTypePreferenceChanged(): boolean {
    return this.tiresFirst !== Helper.isProductTypePreferenceTire();
  }
  setProductTypePreference(value: boolean) {
    this.tiresFirst = value;
  }
 
  makeYearModelChanged(mym: MakeYearModel): boolean {
    return this.packageBuilderMym === undefined ||
      this.packageBuilderMym.make !== mym.make ||
      this.packageBuilderMym.year !== mym.year ||
      this.packageBuilderMym.model !== mym.model ||
      this.packageBuilderMym.submodel !== mym.submodel ||
      this.packageBuilderMym.option !== mym.option ||
      this.packageBuilderMym.isSummerSeason !== mym.isSummerSeason;
  }

  wheelStaggerChanged(stagger: string): boolean {
    return this.wheelStagger === undefined ||
      this.wheelStagger !== stagger;
  }

  tireStaggerChanged(stagger: string): boolean {
    return this.tireStagger === undefined ||
      this.tireStagger !== stagger;
  }

  //tireStaggerInitialChanged(stagger: string): boolean {
  //  return this.tireStaggerInitial === undefined ||
  //    this.tireStaggerInitial !== stagger;
  //}

  navigateToKits() {
    this.log('navigateToKits');
    Helper.setActiveTabState("kit");
    this.setPackageBuilderFlow(PackageBuilderFlow.PACKAGEKITS);
  }

  navigateToWheels() {
    this.log('navigateToWheels');
    Helper.setActiveTabState("whe");
    this.setPackageBuilderFlow(PackageBuilderFlow.WHEELS);
  }

  navigateToTires() {
    this.log('pb.navigateToTires');
    Helper.setActiveTabState("tir");
    this.setPackageBuilderFlow(PackageBuilderFlow.TIRES);
  }

  navigateToHardwares() {
    this.log('pb.navigateToHardwares');
    //djb:i18n: we need a way to keep track of specific tab
    //Helper.setActiveTabState("har");
    this.setPackageBuilderFlow(PackageBuilderFlow.HARDWARE);
  }

  navigateToSummary() {
    this.log('pb.navigateToSummary');
    Helper.setActiveTabState("sum");
    this.setPackageBuilderFlow(PackageBuilderFlow.PACKAGE_BUILDER_SUMMARY);
  }

  updateProduct(product: Product, type: string, summaryProduct:
    PackageBuilderSummaryProduct = null, position: Positions = Positions.FRONT) {
    switch (type) {
      case Constants.PRODUCT_TYPE_WHEEL:
        this.updateWheel(product, position);
        break;
      case Constants.PRODUCT_TYPE_TIRE:
        this.updateTire(product, position);
        break;
      case Constants.PRODUCT_TYPE_HARDWARE:
        this.updateHardware(product, summaryProduct, position);
        break;
      case Constants.PRODUCT_TYPE_LABOUR:
        this.updateLabour(product, summaryProduct, position);
        break;
    }
  }

  removeProduct(product: Product, type: string, summaryProduct: PackageBuilderSummaryProduct, position: Positions = Positions.FRONT) {

    // Here check if there is still something for every type then clear the specific session variable.

    switch (type) {
      case Constants.PRODUCT_TYPE_WHEEL:
        if (!this.tiresFirst) {
          this.clear();
          // djb:ngup8
          //this.onFlowChange.take(1).subscribe(flow => {
          this.onFlowChange.pipe(take(1)).subscribe(flow => {
            if (flow !== PackageBuilderFlow.WHEELS) {
              this.packageBuilderFlowSubject.next(PackageBuilderFlow.WHEELS);
            }
          }, Helper.handleError);
        }
        else
          this.deselectWheels();
        break;
      case Constants.PRODUCT_TYPE_TIRE:
        if (!this.tiresFirst) {
          this.deselectTires();
        }
        else {
          this.clear();
          this.onFlowChange.pipe(take(1)).subscribe(flow => {
            if (flow !== PackageBuilderFlow.TIRES) {
              this.packageBuilderFlowSubject.next(PackageBuilderFlow.TIRES);
            }
          }, Helper.handleError);
        }
        break;
      case Constants.PRODUCT_TYPE_HARDWARE:
        this.removeHardware(product, summaryProduct, position);
        break;
      case Constants.PRODUCT_TYPE_LABOUR:
        this.removeLabour(product, summaryProduct, position);
        break;
    }
  }

  private GetLeadTimeInternalMessage(wheelFromSummary: PackageBuilderSummaryProduct, product: Product) {
    if (wheelFromSummary.inventory.procurementTypeCode !== null && wheelFromSummary.inventory.procurementTypeCode === '1') {
      const actualOnHand = wheelFromSummary.inventory.onHandActualQtyMtl;
      const frontQty = wheelFromSummary.qty;

      const ltNotes: string = this.translateService.instant('FC.LeadTime.Delay.InternalNotes');
      let msg: string = Helper.stringFormat(ltNotes, this.wheelDrillLeadTime.value, this.translateService.instant(`FC.LeadTime.${this.wheelDrillLeadTime.unitOfMeasure}`));

      let restockingMsg = '';
      if (product.brand && product.brand.toLowerCase() === 'braelin')
        restockingMsg = this.translateService.instant('FC.LeadTime.Delay.RestockingFee.InternalNotes');
      
      if (frontQty > actualOnHand) {
        if (wheelFromSummary.warningNotes == null) {
          wheelFromSummary.warningNotes = [];
        }

        if (!wheelFromSummary.warningNotes.some(n => n === msg))
          wheelFromSummary.warningNotes.push(msg);

        if (!wheelFromSummary.warningNotes.some(n => n === restockingMsg) && restockingMsg !== '')
          wheelFromSummary.warningNotes.push(restockingMsg);

      } else {
        if (wheelFromSummary.warningNotes) {
          let ndx: number = wheelFromSummary.warningNotes.findIndex(x => x === msg);
          if (ndx > -1)
            wheelFromSummary.warningNotes.splice(ndx, 1);

          if (restockingMsg !== '') {
            ndx = wheelFromSummary.warningNotes.findIndex(x => x === restockingMsg);
            if (ndx > -1)
              wheelFromSummary.warningNotes.splice(ndx, 1);
          }
        }
      }

      product.warningNotes = wheelFromSummary.warningNotes;
    }
  }

  updateWheel(wheel: Product, position: Positions) {
    const wheelFromModel = position === Positions.FRONT ? this.packageBuilderModel.front.wheel : this.packageBuilderModel.rear.wheel;
    const wheelFromSummary = position === Positions.FRONT ? this.packageBuilderSummary.front.wheel : this.packageBuilderSummary.rear.wheel;

    this.GetLeadTimeInternalMessage(wheelFromSummary, wheelFromModel);

    if (wheelFromModel) {
      wheelFromModel.quantity = wheel.quantity;
    }
    this.packageChangeSubject.next(this.packageBuilderModel);

    if (wheelFromSummary) {
      wheelFromSummary.qty = wheel.quantity;
      wheelFromSummary.totalPrice = wheel.quantity * wheelFromSummary.customerPrice;
    }
    this.packageSummaryChangeSubject.next(this.packageBuilderSummary);
  }

  updateTire(tire: Product, position: Positions) {
    const tireFromModel = position === Positions.FRONT ? this.packageBuilderModel.front.tire : this.packageBuilderModel.rear.tire;
    const tireFromSummary = position === Positions.FRONT ? this.packageBuilderSummary.front.tire : this.packageBuilderSummary.rear.tire;

    if (tireFromModel) {
      tireFromModel.quantity = tire.quantity;
    }
    this.packageChangeSubject.next(this.packageBuilderModel);

    if (tireFromSummary) {
      tireFromSummary.qty = tire.quantity;
      tireFromSummary.totalPrice = tire.quantity * tireFromSummary.customerPrice;
    }
    this.packageSummaryChangeSubject.next(this.packageBuilderSummary);
  }

  updateSideWallType(tireSideWall: TireSideWallDtoResponse, position: Positions) {
    const tireFromModel = position === Positions.FRONT ? this.packageBuilderModel.front.tire : this.packageBuilderModel.rear.tire;
    const tireFromSummary = position === Positions.FRONT ? this.packageBuilderSummary.front.tire : this.packageBuilderSummary.rear.tire;

    if (tireFromModel) {
      tireFromModel['tireSideWall'] = tireSideWall;
    }
    this.packageChangeSubject.next(this.packageBuilderModel);
    
    if (tireFromSummary)
      tireFromSummary.tireSideWallSelected = tireSideWall;
    this.packageSummaryChangeSubject.next(this.packageBuilderSummary);
  }

  removeSideWallType(position: Positions) {
    const tireFromModel = position === Positions.FRONT ? this.packageBuilderModel.front.tire : this.packageBuilderModel.rear.tire;
    const tireFromSummary = position === Positions.FRONT ? this.packageBuilderSummary.front.tire : this.packageBuilderSummary.rear.tire;

    if (tireFromModel) 
      tireFromModel['tireSideWall'] = null;
    this.packageChangeSubject.next(this.packageBuilderModel);
    
    if (tireFromSummary)
      tireFromSummary.tireSideWallSelected = null;
    this.packageSummaryChangeSubject.next(this.packageBuilderSummary);
  }

  updateHardware(hardware: Product, summaryHardware: PackageBuilderSummaryProduct, position: Positions) {
    if (position) {
      if ((hardware.category === AccessoryCategory.Nuts || hardware.category === AccessoryCategory.Bolts)
        && this.packageBuilderModel.front.wheel.warningNotes != null
        && this.packageBuilderModel.front.wheel.warningNotes
          .some(n => n === this.translateService.instant('FC.ProductWarningsNotes.LugsDifferentFromOENotChosen'))) {
        const index = this.packageBuilderModel.front.wheel.warningNotes
          .findIndex(n => n === this.translateService.instant('FC.ProductWarningsNotes.LugsDifferentFromOENotChosen'));
        this.packageBuilderModel.front.wheel.warningNotes.splice(index, 1);
        const summaryIndex = this.packageBuilderSummary.front.wheel.warningNotes
          .findIndex(n => n === this.translateService.instant('FC.ProductWarningsNotes.LugsDifferentFromOENotChosen'));
        this.packageBuilderSummary.front.wheel.warningNotes.splice(summaryIndex, 1);
      }

      if ((hardware.category === AccessoryCategory.HubRings)
        && this.packageBuilderModel.front.wheel.warningNotes != null
        && this.packageBuilderModel.front.wheel.warningNotes
          .some(n => n === this.translateService.instant('FC.ProductWarningsNotes.HubRingsNotChosen'))) {
        const index = this.packageBuilderModel.front.wheel.warningNotes
          .findIndex(n => n === this.translateService.instant('FC.ProductWarningsNotes.HubRingsNotChosen'));
        this.packageBuilderModel.front.wheel.warningNotes.splice(index, 1);
        const summaryIndex = this.packageBuilderSummary.front.wheel.warningNotes
          .findIndex(n => n === this.translateService.instant('FC.ProductWarningsNotes.HubRingsNotChosen'));
        this.packageBuilderSummary.front.wheel.warningNotes.splice(summaryIndex, 1);
      }
      
      if (hardware.category === AccessoryCategory.Mounting)
        this.mountingAdded = true;

      if (hardware.category === AccessoryCategory.Studding) {
        const message = this.translateService.instant('FC.Popup.AluminumStuds');
        this.productNotesService.validateProductWarningsAckNormalPopup([message], null, '', '').then(ack => {
          if (ack) {
            const model = position === Positions.FRONT ?
              this.packageBuilderModel.front.hardwares : this.packageBuilderModel.rear.hardwares;
            const summary = position === Positions.FRONT ?
              this.packageBuilderSummary.front.hardwares : this.packageBuilderSummary.rear.hardwares;
            this.updateProductInProductArray(hardware, model, summaryHardware, summary);
          } else {

          }
        });
      } else {
        const model = position === Positions.FRONT ?
          this.packageBuilderModel.front.hardwares : this.packageBuilderModel.rear.hardwares;
        const summary = position === Positions.FRONT ?
          this.packageBuilderSummary.front.hardwares : this.packageBuilderSummary.rear.hardwares;
        this.updateProductInProductArray(hardware, model, summaryHardware, summary);
        // if bolt/nut already selected and locks are chosen, then decrement bolt/nut quantity accordingly
        //if (hardware.category === AccessoryCategory.WheelLocks) {
        if (hardware.category === AccessoryCategory.LockBolts
          || hardware.category === AccessoryCategory.LockNuts
          || hardware.category === AccessoryCategory.LockNutsET) {
          if (model && summary) {
            const boltNutProduct = model.find(p => p != null
              && (p.category === AccessoryCategory.Bolts || p.category === AccessoryCategory.Nuts));
            const boltNutSummary = summary.find(p => p != null
              && (p.accessoryType === AccessoryCategory.Bolts || p.accessoryType === AccessoryCategory.Nuts));
            if (boltNutProduct && boltNutSummary) {
              if (boltNutProduct.quantity <= boltNutProduct.originalQty && boltNutProduct.originalQty - hardware.quantity * 4 >= 0) {
                boltNutProduct.quantity = boltNutProduct.originalQty - hardware.quantity * 4;
                boltNutSummary.qty = boltNutProduct.quantity;
              }
              this.updateProductInProductArray(boltNutProduct, model, boltNutSummary, summary);
            }
          }
        }
      }
    }
  }

  removeHardware(hardware: Product, summaryHardware: PackageBuilderSummaryProduct, position: Positions) {
    if (position) {
      const model = position === Positions.FRONT ? this.packageBuilderModel.front.hardwares : this.packageBuilderModel.rear.hardwares;
      const summary = position === Positions.FRONT ? this.packageBuilderSummary.front.hardwares : this.packageBuilderSummary.rear.hardwares;
      // if bolt/nut already selected and locks are deselected, then reset bolt/nut quantity accordingly
      //if (hardware.category === AccessoryCategory.WheelLocks) {
      if (hardware.category === AccessoryCategory.LockBolts
        || hardware.category === AccessoryCategory.LockNuts
        || hardware.category === AccessoryCategory.LockNutsET) {
        if (model && summary) {
          const boltNutProduct = model.find(p => p != null
            && (p.category === AccessoryCategory.Bolts || p.category === AccessoryCategory.Nuts));
          const boltNutSummary = summary.find(p => p != null
            && (p.accessoryType === AccessoryCategory.Bolts || p.accessoryType === AccessoryCategory.Nuts));
          if (boltNutProduct && boltNutSummary) {
            if (boltNutProduct.quantity < boltNutProduct.originalQty) {
              boltNutProduct.quantity = boltNutProduct.originalQty;
              boltNutSummary.qty = boltNutProduct.quantity;
            }
          }
          this.updateProductInProductArray(boltNutProduct, model, boltNutSummary, summary);
        }
      }

      if (hardware.category === AccessoryCategory.Mounting)
        this.mountingAdded = false

      this.removeProductFromProductArray(hardware, model, summaryHardware, summary);
    }
  }
  removeKey() {
    if (this.packageBuilderModel.front.hardwares.some(h => h.category === AccessoryCategory.Keys)) {
      const keyProduct = this.packageBuilderModel.front.hardwares.find(h => h.category === AccessoryCategory.Keys);
      const keysummay = this.packageBuilderSummary.front.hardwares.find(h => h.accessoryType === AccessoryCategory.Keys);
      this.removeHardware(keyProduct, keysummay, Positions.FRONT);
    }
  }
  showValvesTab(): boolean {
    const isTPMSSelected = this.packageBuilderModel.front.hardwares.some(h => h.category === AccessoryCategory.TPMS) ||
      this.packageBuilderModel.rear.hardwares.some(h => h.category === AccessoryCategory.TPMS);
    return !isTPMSSelected || (isTPMSSelected && this.valvesRequired);
  }

  updateLabour(labour: Product, summaryLabour: PackageBuilderSummaryProduct, position: Positions) {
    if (position) {
      const model = position === Positions.FRONT ? this.packageBuilderModel.front.labours : this.packageBuilderModel.rear.labours;
      const summary = position === Positions.FRONT ? this.packageBuilderSummary.front.labours : this.packageBuilderSummary.rear.labours;
      this.updateProductInProductArray(labour, model, summaryLabour, summary);
    }
  }

  removeLabour(labour: Product, summaryLabour: PackageBuilderSummaryProduct, position: Positions) {
    if (position) {
      const model = position === Positions.FRONT ? this.packageBuilderModel.front.labours : this.packageBuilderModel.rear.labours;
      const summary = position === Positions.FRONT ? this.packageBuilderSummary.front.labours : this.packageBuilderSummary.rear.labours;
      this.removeProductFromProductArray(labour, model, summaryLabour, summary);
    }
  }
  
  deselectLabour(product: Product): void {
    this.packageBuilderModel.front.labours = this.packageBuilderModel.front.labours.filter((item) => item.id !== product.id);
    this.packageBuilderModel.rear.labours = this.packageBuilderModel.rear.labours.filter((item) => item.id !== product.id);
    this.packageBuilderSummary.front.labours = [];
    this.packageBuilderSummary.rear.labours = [];
  }

  // Bartosz Kosakowski
  // April 28, 2020
  // Checks if there is a different item in the products list that is from the same category
  // and removes it.
  checkForSameCategory(product: Product,
    products: Product[],
    productSummaries: PackageBuilderSummaryProduct[]) {
    let result = new Array<Product>();
    if (product.pairNo != null && product.pairNo.length > 0) {
      products.filter(p => p.pairNo != product.pairNo && p.category == product.category)
        .forEach(p => result.push(p));
    }
    else {
      if (products.some(p => p.id != product.id && p.category == product.category))
        result.push(products.find(p => p.id != product.id && p.category == product.category));
    }
    if (result.length > 0) {
      result.forEach(r => {
        const resultSummary = productSummaries.find(s => s.longSKU == r.id);
        const resultIndex = products.indexOf(r);
        const summaryIndex = productSummaries.indexOf(resultSummary);
        products.splice(resultIndex, 1);
        productSummaries.splice(summaryIndex, 1)
      });
    }
  }

  updateProductInProductArray(product: Product,
    products: Product[],
    productSummary: PackageBuilderSummaryProduct,
    productSummaries: PackageBuilderSummaryProduct[]) {
    if (products) {
      this.checkForSameCategory(product, products, productSummaries);
      const foundProduct = products.find(p => product != null && p != null && p.id === product.id);
      if (foundProduct) {
        if (product.quantity > 0) {
          foundProduct.quantity = product.quantity;
        } else {
          const index = products.indexOf(foundProduct);
          products.splice(index, 1);
        }
      } else {
        products.push(product);
      }
    } else {
      products = [product];
    }
    this.packageChangeSubject.next(this.packageBuilderModel);

    if (productSummaries) {
      const foundSummary = productSummaries.find(p => p != null && productSummary != null && p.shortSKU === productSummary.shortSKU);
      if (foundSummary) {
        if (productSummary.qty > 0) {
          foundSummary.qty = productSummary.qty;
          foundSummary.totalPrice = foundSummary.customerPrice * foundSummary.qty;
        } else {
          const index = productSummaries.indexOf(foundSummary);
          productSummaries.splice(index, 1);
        }
      } else {
        productSummaries.push(productSummary);
      }
    } else {
      productSummaries = [productSummary];
    }
    this.packageSummaryChangeSubject.next(this.packageBuilderSummary);
  }

  removeProductFromProductArray(product: Product,
    products: Product[],
    productSummary: PackageBuilderSummaryProduct,
    productSummaries: PackageBuilderSummaryProduct[]) {
    if (products) {
      let i = products.findIndex(p => p.id === product.id);

      if (i !== -1) {
        products.splice(i, 1);
        if (product.category === AccessoryCategory.Bolts
          || product.category === AccessoryCategory.Nuts) {
          const list = [AccessoryCategory.LockBolts, AccessoryCategory.LockNuts, AccessoryCategory.LockNutsET];
          list.forEach(l => {
            i = products.findIndex(p => p.category === l);
            if (i !== -1)
              products.splice(i, 1);
          });
        }
      }
    }
    this.packageChangeSubject.next(this.packageBuilderModel);

    if (productSummaries) {
      let i = productSummaries.findIndex(p => p.shortSKU === productSummary.shortSKU);

      if (i !== -1) {
        productSummaries.splice(i, 1);
        if (productSummary.accessoryType === AccessoryCategory.Bolts
          || productSummary.accessoryType === AccessoryCategory.Nuts) {
          const list = [AccessoryCategory.LockBolts, AccessoryCategory.LockNuts, AccessoryCategory.LockNutsET];
          list.forEach(l => {
            i = productSummaries.findIndex(p => p.accessoryType === l);
            if (i !== -1)
              productSummaries.splice(i, 1);
          });
        }
      }
    }
    this.packageSummaryChangeSubject.next(this.packageBuilderSummary);
  }

  postPackageToQuote() {
    if (this.packageBuilderMym !== null &&
      (this.packageBuilderModel.front.wheel !== null ||
        this.packageBuilderModel.front.tire !== null ||
        (Array.isArray(this.packageBuilderModel.front.hardwares)))) {

      const api = `${this.apiCmsEndpoint}/cart/QuotePackage`;

      const packageBuilderPackage: PackageBuilderPackage = new PackageBuilderPackage();
      packageBuilderPackage.makeYearModel = this.packageBuilderMym;
      packageBuilderPackage.package.front = this.packageBuilderModel.front;
      packageBuilderPackage.package.rear = this.packageBuilderModel.rear;

      this.http.post(api, packageBuilderPackage).toPromise().then(response => {
        window.location.href = Constants.QUOTES_HISTORY_HREF;
      }).catch((err: any) => {
        window.location.href = Constants.ORDERS_HISTORY_HREF;
      });

    }
  }

  savePackageSummary() {
    if (this.packageBuilderMym !== null
      && this.packageBuilderModel.front.wheel !== null && this.packageBuilderModel.front.tire !== null && this.packageBuilderModel.front.labours !== null && this.packageBuilderModel.front.labours.length !== 0
      && (this.packageBuilderModel.rear.wheel === null && this.packageBuilderModel.rear.tire === null
        || (this.packageBuilderModel.rear.wheel !== null && this.packageBuilderModel.rear.tire !== null))
      && (((this.isVehicleHaveTPMS() === false || this.packageBuilderOEMData.groups.some(g => g.items.some(i => i.itemName === 'Tpms' && (i.itemValue === 'Direct: Yes - Optional' || i.itemValue === 'Direct: Oui - Facultatif'))))
                   && this.packageBuilderModel.front.hardwares.some(h => h.category === AccessoryCategory.TireValves))
          || (this.packageBuilderModel.front.hardwares.some(h => h.category === AccessoryCategory.TPMS)
                         && (this.packageBuilderModel.rear.hardwares.length === 0 
                              || this.packageBuilderModel.rear.hardwares.some(h => h.category === AccessoryCategory.TPMS)))
             ))
        /*(Array.isArray(this.packageBuilderModel.front.hardwares))*/{
      const api = `${this.apiCmsEndpoint}/cart/Summary/Package`;

      const packageBuilderPackage: PackageBuilderPackage = new PackageBuilderPackage();
      packageBuilderPackage.makeYearModel = this.packageBuilderMym;
      packageBuilderPackage.package.front = this.packageBuilderModel.front;
      packageBuilderPackage.package.rear = this.packageBuilderModel.rear;

      this.http.post(api, packageBuilderPackage).toPromise().then(response => {
        window.location.href = Constants.PACKAGEBUILDER_HOME;
      }).catch((err: any) => {
        window.location.href = Constants.PACKAGEBUILDER_HOME;
      });
    }
    else {
      this.productNotesService.validateProductWarningsAckNormalPopup([this.translateService.instant('FC.Popup.SavePackageSummary.Requirement')],
        null, '', '', true).then(ack => { });
    }
  }

  async postPackageToCart() {
    if (this.packageBuilderMym !== null &&
      (this.packageBuilderModel.front.wheel !== null ||
        this.packageBuilderModel.front.tire !== null ||
        (Array.isArray(this.packageBuilderModel.front.hardwares)))) {
      if (this.packageBuilderMym.isConcept === true
        || (this.packageBuilderSummary.front.wheel !== null && this.packageBuilderSummary.front.wheel.isConcept === true)
        || (this.packageBuilderSummary.front.tire !== null && this.packageBuilderSummary.front.tire.isConcept === true)
        || (this.packageBuilderSummary.rear !== null && this.packageBuilderSummary.rear.wheel !== null && this.packageBuilderSummary.rear.wheel.isConcept === true)
        || (this.packageBuilderSummary.rear !== null && this.packageBuilderSummary.rear.tire !== null && this.packageBuilderSummary.rear.tire.isConcept === true)
      ) {
        this.productNotesService.validateProductWarningsAckNormalPopup([this.translateService.instant('FC.Popup.Concept.PackageContent')],
          null, '', '', true).then(ack => { });
      } else if ((this.packageBuilderSummary.front.wheel !== null && this.packageBuilderSummary.front.wheel.dealerCost === 9999.99)
        || (this.packageBuilderSummary.front.tire !== null && this.packageBuilderSummary.front.tire.dealerCost === 9999.99)
        || (this.packageBuilderSummary.front.hardwares !== null && this.packageBuilderSummary.front.hardwares.some(h => h.dealerCost === 9999.99))
        || (this.packageBuilderSummary.rear !== null && this.packageBuilderSummary.rear.wheel !== null && this.packageBuilderSummary.rear.wheel.dealerCost === 9999.99)
        || (this.packageBuilderSummary.rear !== null && this.packageBuilderSummary.rear.tire !== null && this.packageBuilderSummary.rear.tire.dealerCost === 9999.99)
        || (this.packageBuilderSummary.rear !== null && this.packageBuilderSummary.rear.hardwares !== null && this.packageBuilderSummary.rear.hardwares.some(h => h.dealerCost === 9999.99))
      ) {
        this.productNotesService.validateProductWarningsAckNormalPopup([this.translateService.instant('FC.Popup.MissingMSRP.PackageContent')],
          null, '', '', true).then(ack => { });
      }
      else {

        const popups = new Array<PopupWarning>();
        if (this.isVehicleHaveTPMS()
          && !this.packageBuilderModel.front.hardwares.some(h => h.category === AccessoryCategory.TPMS)
          && (this.packageBuilderModel.rear == null
            || !this.packageBuilderModel.rear.hardwares.some(h => h.category === AccessoryCategory.TPMS))
          && this.TPMSAvailable) {
          popups.push(<PopupWarning>{
            popupText: this.translateService.instant('FC.Popup.TPMSPrompt'),
            warningNote: this.translateService.instant('FC.Popup.TPMSPrompt.Note'),
            packageBuilderFlow: PackageBuilderFlow.HARDWARE,
            tabId: AccessoryCategory.TPMS,
            continueButtonSuffix: 'FC.Popup.TPMSPrompt.Yes',
            cancelButtonSuffix: 'FC.Popup.TPMSPrompt.No'
          });
        }

        //const isVehicleNotTPMSEquipped = !this.isVehicleHaveTPMS();
        //const isTPMSNotAvailable = !this.TPMSAvailable;
        const mountingSelected = this.packageBuilderModel.front.hardwares.some(h => h.category === AccessoryCategory.Mounting);
        const frontTpmsSelected = this.packageBuilderModel.front.hardwares.some(h => h.category === AccessoryCategory.TPMS);
        const valveSelected = this.packageBuilderModel.front.hardwares.some(h => h.category === AccessoryCategory.TireValves);

        let isFrontTpmsBand = false;
        if (frontTpmsSelected)
          isFrontTpmsBand = this.packageBuilderModel.front.hardwares.filter(x => x.category === AccessoryCategory.TPMS)[0].id.toUpperCase() === 'DL5105';

        if (mountingSelected && (!this.isVehicleHaveTPMS() || !this.TPMSAvailable) || isFrontTpmsBand) {
          let pushMsg = false;

          if (!frontTpmsSelected && !valveSelected)
            pushMsg = true;

          if (isFrontTpmsBand && !valveSelected)
            pushMsg = true;

          if (pushMsg) {
            popups.push(<PopupWarning>{
              popupText: this.translateService.instant('FC.Popup.ValvesPrompt'),
              warningNote: '',
              packageBuilderFlow: PackageBuilderFlow.HARDWARE,
              tabId: AccessoryCategory.TireValves,
              continueButtonSuffix: 'FC.Popup.ValesPrompt.Yes',
              cancelButtonSuffix: null
            });
          }
        }

        if (popups.length >= 1) {

        }
        else {
          if (this.keysRequired
            && !this.packageBuilderModel.front.hardwares.some(h => h.category === AccessoryCategory.Keys)
            && (this.packageBuilderModel.rear === null
              || !this.packageBuilderModel.rear.hardwares.some(h => h.category === AccessoryCategory.Keys))) {
            popups.push(<PopupWarning>{
              popupText: this.translateService.instant('FC.Popup.KeysRequired'),
              warningNote: this.translateService.instant('FC.ProductWarningsNotes.KeysRequiredNotChosen'),
              packageBuilderFlow: PackageBuilderFlow.HARDWARE,
              tabId: AccessoryCategory.Keys,
              continueButtonSuffix: 'FC.Popup.KeysRequired.Yes',
              cancelButtonSuffix: 'FC.Popup.KeysRequired.No'
            });
          }
        }
        let returnTab = '';
        let flow = PackageBuilderFlow.NONE;
        for (let i = 0; i < popups.length; i++) {
          const popup = popups[i];
          const validated = await this.productNotesService.validateProductWarningsAckNormalPopup([popup.popupText],
            null, popup.continueButtonSuffix, popup.cancelButtonSuffix);
          if (validated) {
            if (flow === PackageBuilderFlow.NONE) {
              returnTab = popup.tabId.toString();
              flow = popup.packageBuilderFlow;

              //Set TPMS active tab if warning is acknowledged
              if (returnTab == "68") {
                $('.b2b-nav-tabs li a').removeClass('activePBFlow');
                $('#tpm').addClass('activePBFlow');
                popups.length == 0;
              }

              if (returnTab == "90") {
                $('.b2b-nav-tabs li a').removeClass('activePBFlow');
                $('#key').addClass('activePBFlow');
              }

              if (returnTab == AccessoryCategory.TireValves.toString()) {
                $('.b2b-nav-tabs li a').removeClass('activePBFlow');
                $('#tva').addClass('activePBFlow');
                popups.length == 0;
              }
            }
          }
          else {
            if (popup.tabId != AccessoryCategory.Keys && this.keysRequired
              && !this.packageBuilderModel.front.hardwares.some(h => h.category === AccessoryCategory.Keys)
              && (this.packageBuilderModel.rear === null
                || !this.packageBuilderModel.rear.hardwares.some(h => h.category === AccessoryCategory.Keys))) {
              popups.push(<PopupWarning>{
                popupText: this.translateService.instant('FC.Popup.KeysRequired'),
                warningNote: this.translateService.instant('FC.ProductWarningsNotes.KeysRequiredNotChosen'),
                packageBuilderFlow: PackageBuilderFlow.HARDWARE,
                tabId: AccessoryCategory.Keys,
                continueButtonSuffix: 'FC.Popup.KeysRequired.Yes',
                cancelButtonSuffix: 'FC.Popup.KeysRequired.No'
              });
            }

            // User choose not to add TPMS in the package: If Mounting is added in the package, TPMS or Valve should be in the package
            if (this.isVehicleHaveTPMS() && mountingSelected && !frontTpmsSelected && !valveSelected) {
              popups.push(<PopupWarning>{
                popupText: this.translateService.instant('FC.Popup.ValvesPrompt'),
                warningNote: '',
                packageBuilderFlow: PackageBuilderFlow.HARDWARE,
                tabId: AccessoryCategory.TireValves,
                continueButtonSuffix: 'FC.Popup.ValesPrompt.Yes',
                cancelButtonSuffix: null
              });
            }

            if (popup.warningNote !== '') {
              if (this.packageBuilderModel.front.wheel.warningNotes == null)
                this.packageBuilderModel.front.wheel.warningNotes = [];
              this.packageBuilderModel.front.wheel.warningNotes.push(popup.warningNote);
            }
          }

        }

        if (flow === PackageBuilderFlow.NONE) {
          this.sendToCart();
        } else {
          if (returnTab !== '') {
            // djb:should this be the actual tab name or number?
            sessionStorage.setItem('SelectedTab', returnTab);
          }
          this.setPackageBuilderFlow(flow);
        }
      }
    }
   
  }

  private sendToCart() {
    const packageBuilderPackage: PackageBuilderPackage = new PackageBuilderPackage();
    let ids: string[] = [];
    this.removeManualServiceItems();
    packageBuilderPackage.makeYearModel = this.packageBuilderMym;
    packageBuilderPackage.searchLogId = this.searchLogId;
    packageBuilderPackage.package.front.wheel = this.packageBuilderModel.front.wheel != null && this.verifyIsProductValid(this.packageBuilderSummary.front.wheel) ?
      this.packageBuilderModel.front.wheel : null;
    packageBuilderPackage.package.front.tire = this.packageBuilderModel.front.tire != null && this.verifyIsProductValid(this.packageBuilderSummary.front.tire) ?
      this.packageBuilderModel.front.tire : null;
    packageBuilderPackage.package.front.labours = this.packageBuilderModel.front.labours;

    packageBuilderPackage.package.rear.wheel = this.packageBuilderModel.rear.wheel != null && this.verifyIsProductValid(this.packageBuilderSummary.rear.wheel) ?
      this.packageBuilderModel.rear.wheel : null;
    packageBuilderPackage.package.rear.tire = this.packageBuilderModel.rear.tire != null && this.verifyIsProductValid(this.packageBuilderSummary.rear.tire) ?
      this.packageBuilderModel.rear.tire : null;
    packageBuilderPackage.package.rear.labours = this.packageBuilderModel.rear.labours;

    // explode hardware kits, if any
    if (this.packageBuilderModel.front.hardwares != null && this.packageBuilderModel.front.hardwares.length > 0)
      this.packageBuilderModel.front.hardwares.forEach(h => {
        if (!ids.some(i => i === h.id))
          ids.push(h.id);
      });

    if (this.packageBuilderModel.rear.hardwares != null && this.packageBuilderModel.rear.hardwares.length > 0)
      this.packageBuilderModel.rear.hardwares.forEach(h => {
        if (!ids.some(i => i === h.id))
          ids.push(h.id);
      });

    if (ids.length > 0) {
      this.searchComponents(ids);

      this.onComponents.subscribe(
        responseProduct => {
          if (responseProduct != null) {
            this.packageBuilderModel.front.hardwares.forEach(h => {
              if (!responseProduct.some(i => i.id === h.id))
                packageBuilderPackage.package.front.hardwares.push(h);
              else {
                const componentsDto = responseProduct.find(i => i.id === h.id);
                h.type = Constants.PRODUCT_TYPE_KIT;
                h.isKit = true;
                h.kitID = h.id;
                packageBuilderPackage.package.front.hardwares.push(h);

                componentsDto.cards.forEach(c => {
                  const productType = Constants.PRODUCT_TYPE_HARDWARE;
                  const prod = new Product({
                    id: c.id,
                    warningNotes: null,
                    quantity: c.kitQty * h.quantity,
                    type: productType,
                    pairNo: "",
                    //sequence: sequence
                    isKitComponent: true,
                    kitID: componentsDto.id
                  });

                  packageBuilderPackage.package.front.hardwares.push(prod);
                });
              }
            });
            this.packageBuilderModel.rear.hardwares.forEach(h => {
              if (!responseProduct.some(i => i.id === h.id))
                packageBuilderPackage.package.rear.hardwares.push(h);
              else {
                const componentsDto = responseProduct.find(i => i.id === h.id);
                h.type = Constants.PRODUCT_TYPE_KIT;
                h.isKit = true;
                h.kitID = h.id;
                packageBuilderPackage.package.rear.hardwares.push(h);

                componentsDto.cards.forEach(c => {
                  const productType = Constants.PRODUCT_TYPE_HARDWARE;
                  const prod = new Product({
                    id: c.id,
                    warningNotes: null,
                    quantity: c.kitQty * h.quantity,
                    type: productType,
                    pairNo: "",
                    //sequence: sequence
                    isKitComponent: true,
                    kitID: componentsDto.id
                  });

                  packageBuilderPackage.package.rear.hardwares.push(prod);
                });
              }
            });
          }

          let api = `${this.apiCmsEndpoint}/cart/AddPackage`;

            // WIP - SEASONAL CARE PLAN
            if (this.customParameter !== null && this.customParameter['scpReplaceCartContent'] !== null) {
                const _scpReplaceCartContent = this.customParameter['scpReplaceCartContent'];
                if (_scpReplaceCartContent)
                    api = `${this.apiCmsEndpoint}/cart/AddPackage?scpReplaceCartContent=true`;
            }

          this.http.post(api, packageBuilderPackage).toPromise().then(response => {
            window.location.href = Constants.SHOPPING_CART_HREF;
          }).catch((err: any) => {
            window.location.href = Constants.SHOPPING_CART_HREF;
          });
        }
      );
    }
    else {
      let api = `${this.apiCmsEndpoint}/cart/AddPackage`;

        // WIP - SEASONAL CARE PLAN
        if (this.customParameter !== null && this.customParameter['scpReplaceCartContent'] !== null) {
            const _scpReplaceCartContent = this.customParameter['scpReplaceCartContent'];
            if (_scpReplaceCartContent)
                api = `${this.apiCmsEndpoint}/cart/AddPackage?scpReplaceCartContent=true`;
        }

      this.http.post(api, packageBuilderPackage).toPromise().then(response => {
        window.location.href = Constants.SHOPPING_CART_HREF;
      }).catch((err: any) => {
        window.location.href = Constants.SHOPPING_CART_HREF;
        });
    }
  }

  // djb:hack-to prevent posting duplicate hardware items (they are in labours)
  // only used by the hardware grid, they need to be removed from package before posting to cart
  // when this is resolved using a different technique, this function can be removed
  removeManualServiceItems(): void {
    this.packageBuilderModel.front.hardwares = this.packageBuilderModel.front.hardwares.filter(h => h.category !== AccessoryCategory.Mounting && h.category !== AccessoryCategory.Studding);
    this.packageBuilderModel.rear.hardwares = this.packageBuilderModel.rear.hardwares.filter(h => h.category !== AccessoryCategory.Mounting && h.category !== AccessoryCategory.Studding);
  }

  getMountingAndBalancingQty(tireQty: number, position?: Positions): number {
    if (position === Positions.REAR) {
      return Math.min(this.packageBuilderModel.rear.wheel.quantity, tireQty);
    }
    return Math.min(this.packageBuilderModel.front.wheel.quantity, tireQty);
  }

  HubCapSelected(): boolean {
    return this.hasHardwareOfType(AccessoryCategory.HubCaps);
  }

  hasHardwareOfType(categoryId: Number): boolean {
    return this.packageBuilderModel.front.hardwares.some(h => h != null && h.category === categoryId) ||
      this.packageBuilderModel.rear.hardwares.some(h => h != null && h.category === categoryId);
  }

  getOEMData(): Promise<VehicleOemDataDtoResponse> {
    this.log('getOemData');
    return this.mayeYearModelService.getCarOemData(this.packageBuilderMym);
  }

  WheelOETireSizeFitment(frontWheel: string, rearWheel: string, frontOffset: string, rearOffset: string,): Observable<OETireSizeFitmentDtoResponse> {
    return this.tireService.getOETireSizeFitment(this.packageBuilderMym, frontWheel, rearWheel, frontOffset, rearOffset);
  }

  addNoteToWheel(note: string, position?: Positions): void {
    if (position === Positions.REAR) {
      if (this.packageBuilderModel.rear.wheel.warningNotes == null) {
        this.packageBuilderModel.rear.wheel.warningNotes = [];
      }
      if (!this.packageBuilderModel.rear.wheel.warningNotes.some(n => n === note)) {
        this.packageBuilderModel.rear.wheel.warningNotes.push(note);
      }
      if (this.packageBuilderSummary.rear.wheel.warningNotes == null) {
        this.packageBuilderSummary.rear.wheel.warningNotes = [];
      }
      if (!this.packageBuilderSummary.rear.wheel.warningNotes.some(n => n === note)) {
        this.packageBuilderSummary.rear.wheel.warningNotes.push(note);
      }
    }
    else {
      if (this.packageBuilderModel.front.wheel.warningNotes == null) {
        this.packageBuilderModel.front.wheel.warningNotes = [];
      }
      if (!this.packageBuilderModel.front.wheel.warningNotes.some(n => n === note)) {
        this.packageBuilderModel.front.wheel.warningNotes.push(note);
      }
      if (this.packageBuilderSummary.front.wheel.warningNotes == null) {
        this.packageBuilderSummary.front.wheel.warningNotes = [];
      }
      if (!this.packageBuilderSummary.front.wheel.warningNotes.some(n => n === note)) {
        this.packageBuilderSummary.front.wheel.warningNotes.push(note);
      }
    }
  }
  removeOETireNote(): void {
    if (this.IsWheelAndTireSelected()) {
      if (this.packageBuilderModel.front.wheel.warningNotes != null && this.packageBuilderModel.front.wheel.warningNotes.some(n => n === this.oeTireSizeFitmentNote.front)) {
        this.packageBuilderModel.front.wheel.warningNotes = this.packageBuilderModel.front.wheel.warningNotes.filter(n => n !== this.oeTireSizeFitmentNote.front)
        this.packageBuilderSummary.front.wheel.warningNotes = this.packageBuilderSummary.front.wheel.warningNotes.filter(n => n !== this.oeTireSizeFitmentNote.front)
      }
      if (this.packageBuilderModel.rear !== null && this.packageBuilderModel.rear.wheel != null
        && this.packageBuilderModel.rear.wheel.warningNotes != null && this.packageBuilderModel.rear.wheel.warningNotes.some(n => n === this.oeTireSizeFitmentNote.rear)) {
        this.packageBuilderModel.rear.wheel.warningNotes = this.packageBuilderModel.rear.wheel.warningNotes.filter(n => n !== this.oeTireSizeFitmentNote.rear)
        this.packageBuilderSummary.rear.wheel.warningNotes = this.packageBuilderSummary.rear.wheel.warningNotes.filter(n => n !== this.oeTireSizeFitmentNote.rear)
      }
    }
    else {
      if (this.oeTireSizeFitmentNote.front !== null && this.packageBuilderModel.front.wheel !== null
        && (this.packageBuilderModel.front.wheel.warningNotes === null || !this.packageBuilderModel.front.wheel.warningNotes.some(n => n === this.oeTireSizeFitmentNote.front))) {
        if (this.packageBuilderModel.front.wheel.warningNotes === null) {
          this.packageBuilderModel.front.wheel.warningNotes = [];
        }
        this.packageBuilderModel.front.wheel.warningNotes.push(this.oeTireSizeFitmentNote.front);
        if (this.packageBuilderSummary.front.wheel.warningNotes == null) {
          this.packageBuilderSummary.front.wheel.warningNotes = [];
        }
        if (!this.packageBuilderSummary.front.wheel.warningNotes.some(n => n === this.oeTireSizeFitmentNote.front)) {
          this.packageBuilderSummary.front.wheel.warningNotes.push(this.oeTireSizeFitmentNote.front);
        }
      }
      if (this.oeTireSizeFitmentNote.rear !== null && this.packageBuilderModel.rear !== null && this.packageBuilderModel.rear.wheel !== null
        && (this.packageBuilderModel.rear.wheel.warningNotes === null || !this.packageBuilderModel.rear.wheel.warningNotes.some(n => n === this.oeTireSizeFitmentNote.rear))) {
        if (this.packageBuilderModel.rear.wheel.warningNotes === null) {
          this.packageBuilderModel.rear.wheel.warningNotes = [];
        }
        this.packageBuilderModel.rear.wheel.warningNotes.push(this.oeTireSizeFitmentNote.rear);

        if (this.packageBuilderSummary.rear.wheel.warningNotes == null) {
          this.packageBuilderSummary.rear.wheel.warningNotes = [];
        }
        if (!this.packageBuilderSummary.rear.wheel.warningNotes.some(n => n === this.oeTireSizeFitmentNote.rear)) {
          this.packageBuilderSummary.rear.wheel.warningNotes.push(this.oeTireSizeFitmentNote.rear);
        }
      }
    }
  }
  getTranslatedNote(key: string): string {
    return this.translateService.instant(key);
  }

  validateWarnings(messages: string[], index = 0): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      if (index >= messages.length) {
        resolve(true);
      } else {
        this.productNotesService.validateProductWarningsAckNormalPopup([messages[index]], null, '', '', false, this.showSkip).then(ack => {
          if (ack) {
            index += 1;
            this.validateWarnings(messages, index).then(validated => {
              resolve(validated);
            });
          } else {
            resolve(false);
          }
        });
      }
    });
  }

  getHubRingMessage(): string {
    let message = '';
    if (this.frontHubRingRequired && this.rearHubRingRequired) {
      message = this.translateService.instant('FC.Popup.HubRingsRequired.Both');
    } else if (this.frontHubRingRequired) {
      message = this.translateService.instant('FC.Popup.HubRingsRequired.Front');
    } else if (this.rearHubRingRequired) {
      message = this.translateService.instant('FC.Popup.HubRingsRequired.Rear');
    }
    return message;
  }
  WheelBoltsNutsInfo(frontWheel: string, rearWheel: string): Observable<BoltsNutsInfos> {
    return this.wheelService.getWheelBoltsNutsInfo(this.packageBuilderMym, frontWheel, rearWheel);
  }
  getBoltsNutsRequirements(boltsNutsInfos: BoltsNutsInfos): string {
    // djb:yagni-oemData not used, one cause of extra oemData calls
    //const oemData = this.mayeYearModelService.getCarOemData(this.packageBuilderMym);
    if (this.packageBuilderModel.front.wheel && this.packageBuilderModel.front.wheel.id.startsWith('OEM')) {
      let message: string = "";
      const boltsOrNuts = this.packageBuilderOEMData.groups.find(g => g.groupName === 'Lugs')
        .items.find(i => i.itemName === 'BoltNut').itemValue;
      message = this.translateService.instant(`FC.WarningMessage.ShankRange_Text_OEM${boltsOrNuts}`);
      if (boltsNutsInfos.oemSeats.length > 1)
        message += '\n' + this.translateService.instant(`FC.WarningMessage.ShankRange_Text_OEM${boltsOrNuts}_MultiSeat`).replace('{0}', boltsNutsInfos.oemSeats.join(this.translateService.instant('FC.Separator.Or')));
      if (this.wheelIsDirectFit) {
        message += '\n' + this.translateService.instant(`FC.WarningMessage.DirectFit_${boltsOrNuts}`);
      }
      return message;
    }
    else {
      const hex = this.packageBuilderOEMData.groups.find(g => g.groupName === 'Lugs').items.find(i => i.itemName === 'Hex').itemValue;
      const seat = this.wheelSeat;
      const diameter = boltsNutsInfos.maxDiameter != null ? boltsNutsInfos.maxDiameter : 'N/A';
      const thread = this.packageBuilderOEMData.groups.find(g => g.groupName === 'Lugs').items.find(i => i.itemName === 'Thread').itemValue;
      const firstPart = this.translateService.instant('FC.WarningMessage.BoltsNutsRequirements')
        .replace('{0}', hex).replace('{1}', seat).replace('{2}', diameter).replace('{3}', thread).replace('<br/>', ' ');
      let secondPart = '';
      const boltsOrNuts = this.packageBuilderOEMData.groups.find(g => g.groupName === 'Lugs')
        .items.find(i => i.itemName === 'BoltNut').itemValue;
      if (boltsOrNuts === 'Bolt' || boltsOrNuts === 'Boulon') {
        secondPart = this.translateService.instant('FC.WarningMessage.ShankRange_Text_Bolt')
          .replace('{0}', boltsNutsInfos.shankFrom).replace('{1}', boltsNutsInfos.shankTo);
      } else if (boltsOrNuts === 'Nut' || boltsOrNuts === 'Écrou') {
        if (this.wheelSeat === 'Flat' || this.wheelSeat === 'Flat Seat' || this.wheelSeat === 'Plat') {
          secondPart = this.translateService.instant('FC.WarningMessage.ShankRange_Text_Nut_Flat')
            .replace('{0}', boltsNutsInfos.shankFrom)
            .replace('{1}', boltsNutsInfos.minBoltHoleSize)
            .replace('{2}', boltsNutsInfos.boltHoleSize)
            .replace('{3}', boltsNutsInfos.seatThickness);
        } else if (this.wheelIsOpenEnded || boltsNutsInfos.shankFrom == null) {
          secondPart = this.translateService.instant('FC.WarningMessage.ShankRange_Text_Nut_OpenEnded');
        } else if (this.wheelIsSteel) {
          secondPart = this.translateService.instant('FC.WarningMessage.ShankRange_Text_Nut_Steel').replace('{0}', boltsNutsInfos.shankFrom);
        } else {
          secondPart = this.translateService.instant('FC.WarningMessage.ShankRange_Text_Nut').replace('{0}', boltsNutsInfos.shankFrom);
        }
      }
      if (this.wheelIsDirectFit)
        return (firstPart + '\n' + secondPart + '\n' + this.translateService.instant(`FC.WarningMessage.DirectFit_${boltsOrNuts}`)).trim();
      return (firstPart + '\n' + secondPart).trim();
    }
    return null;
  }
  getBoltsNutsWarningMessage(boltsNutsInfos: BoltsNutsInfos): string {
    const hex = this.packageBuilderOEMData.groups.find(g => g.groupName === 'Lugs').items.find(i => i.itemName === 'Hex').itemValue;
    const seat = this.wheelSeat;
    const diameter = boltsNutsInfos.maxDiameter != null ? boltsNutsInfos.maxDiameter : 'N/A';
    const thread = this.packageBuilderOEMData.groups.find(g => g.groupName === 'Lugs').items.find(i => i.itemName === 'Thread').itemValue;
    const firstPart = this.translateService.instant('FC.WarningMessage.BoltsNutsRequirements')
      .replace('{0}', hex).replace('{1}', seat).replace('{2}', diameter).replace('{3}', thread).replace('<br/>', ' ');
    let secondPart = '';
    const boltsOrNuts = this.packageBuilderOEMData.groups.find(g => g.groupName === 'Lugs')
      .items.find(i => i.itemName === 'BoltNut').itemValue;
    let heading = '';
    //console.log(boltsOrNuts + this.wheelSeat);
    if (boltsOrNuts === 'Bolt' || boltsOrNuts === 'Boulon') {
      heading = this.translateService.instant('FC.WarningMessage.Heading_Bolts_Unavailable');
      secondPart = this.translateService.instant('FC.WarningMessage.ShankRange_Text_Bolt')
        .replace('{0}', boltsNutsInfos.shankFrom).replace('{1}', boltsNutsInfos.shankTo);
    } else if (boltsOrNuts === 'Nut' || boltsOrNuts === 'Écrou') {
      heading = this.translateService.instant('FC.WarningMessage.Heading_Nuts_Unavailable');
      if (this.wheelSeat === 'Flat' || this.wheelSeat === 'Flat Seat' || this.wheelSeat === 'Plat') {
        secondPart = this.translateService.instant('FC.WarningMessage.ShankRange_Text_Nut_Flat')
          .replace('{0}', boltsNutsInfos.shankFrom)
          .replace('{1}', boltsNutsInfos.minBoltHoleSize)
          .replace('{2}', boltsNutsInfos.boltHoleSize)
          .replace('{3}', boltsNutsInfos.seatThickness);
      } else if (this.wheelIsOpenEnded || boltsNutsInfos.shankFrom == null) {
        secondPart = this.translateService.instant('FC.WarningMessage.ShankRange_Text_Nut_OpenEnded');
      } else if (this.wheelIsSteel) {
        secondPart = this.translateService.instant('FC.WarningMessage.ShankRange_Text_Nut_Steel').replace('{0}', boltsNutsInfos.shankFrom);
      } else {
        secondPart = this.translateService.instant('FC.WarningMessage.ShankRange_Text_Nut').replace('{0}', boltsNutsInfos.shankFrom);
      }
    }
    if (this.wheelIsDirectFit)
      return (heading + '\n' + firstPart + '\n' + secondPart + '\n' + this.translateService.instant(`FC.WarningMessage.DirectFit_${boltsOrNuts}`)).trim();
    return (heading + '\n' + firstPart + '\n' + secondPart).trim();
  }

  differentLockHex(description: string, hardwareDetails: ProductDetail[] = null, hardwareLabel: string = null): boolean {
    const oemHexSize = this.packageBuilderOEMData.groups
      .find(g => g.groupName === 'Lugs').items.find(i => i.itemName === 'Hex').itemValue.split('mm')[0].trim();

    let hexSizesFromDescription: String[] = null;

    if (hardwareDetails !== null) {
      const _description = hardwareDetails.find(x => x.label.toUpperCase().endsWith(hardwareLabel.toUpperCase()));

      if (_description)
        hexSizesFromDescription = _description.value.replace('mm', '').split('/');
    }

    if (hexSizesFromDescription === null || hexSizesFromDescription.length === 0)
      hexSizesFromDescription = Helper.extractHexSizesFromDescription(description);

    return !hexSizesFromDescription.some(hs => hs === oemHexSize);
    //!Helper.extractHexSizesFromDescription(description).some(hs => hs === oemHexSize);
  }

  differentBoltNutHex(hex: string): boolean {
    const oemHexSize = this.packageBuilderOEMData.groups
      .find(g => g.groupName === 'Lugs').items.find(i => i.itemName === 'Hex').itemValue.split('mm')[0].trim();
    return hex.replace('mm','').trim() != oemHexSize;
  }

  populatePackage(pbModel: StaggeredPackageBuilderModel, flow: PackageBuilderFlow,
    frontSummary: PackageBuilderSelections, rearSummary: PackageBuilderSelections, oemData: VehicleOemDataDtoResponse) {
    this.packageBuilderOEMData = oemData;
    this.packageBuilderModel = pbModel;
    this.packageBuilderSummary = <StaggeredPackageBuilderSummary>{
      front: <PackageBuilderSummary>{
        wheel: frontSummary != null ? frontSummary.selectedWheel : null,
        tire: frontSummary != null ? frontSummary.selectedTire : null,
        hardwares: frontSummary != null ? frontSummary.selectedHardwares : null,
        labours: frontSummary != null ? frontSummary.selectedLabours : null
      },
      rear: <PackageBuilderSummary>{
        wheel: rearSummary != null ? rearSummary.selectedWheel : null,
        tire: rearSummary != null ? rearSummary.selectedTire : null,
        hardwares: rearSummary != null ? rearSummary.selectedHardwares : null,
        labours: rearSummary != null ? rearSummary.selectedLabours : null
      }

    };
    this.log('populatePackage');
    this.packageChangeSubject.next(this.packageBuilderModel);
    this.packageSummaryChangeSubject.next(this.packageBuilderSummary);
    this.packageBuilderFlow = flow;
    this.packageBuilderFlowSubject.next(this.packageBuilderFlow);
  }

  getPackageBuilderOEMData(): VehicleOemDataDtoResponse {
    return this.packageBuilderOEMData;
  }

  notifyWheelRemoved() {
    this.wheelRemoved.next(true);
  }

  notifyTireRemoved() {
    this.tireRemoved.next(true);
  }

  // djb:dry-interesting duplication flowStatus - verify usage
  public getPackageBuilderFlow(): PackageBuilderFlow {
    return this.packageBuilderFlow;
  }

  getPackageBuilderFlowStatus(): PackageBuilderFlow {
    return this.packageBuilderFlow;
  }

  public setPackageBuilderFlow(flow: PackageBuilderFlow) {
    const msg = `setPackageBuilderFlow: ${this.packageBuilderFlow} -> ${flow}`
    if (this.packageBuilderFlow === flow) {
      this.log(`${msg} IGNORED`);
    } else {
      this.log(`${msg}`);
      this.packageBuilderFlow = flow;
      this.packageBuilderFlowSubject.next(flow);
    }
  }

  public clearServiceCards(): void {
    this.log('clearServiceCards');
    this.serviceSummaryCards = new Array<GenericSummaryCardDtoResponse>();
    this.packageBuilderModel.front.hardwares = this.packageBuilderModel.front.hardwares.filter(h => h.category !== AccessoryCategory.Mounting);
    this.packageBuilderModel.front.hardwares = this.packageBuilderModel.front.hardwares.filter(h => h.category !== AccessoryCategory.Studding);
    this.packageBuilderModel.rear.hardwares = this.packageBuilderModel.rear.hardwares.filter(h => h.category !== AccessoryCategory.Mounting);
    this.packageBuilderModel.rear.hardwares = this.packageBuilderModel.rear.hardwares.filter(h => h.category !== AccessoryCategory.Studding);
  }

  public conceptPopup(): void {
    this.productNotesService.validateProductWarningsAckNormalPopup(
      [this.translateService.instant('FC.Popup.ConceptProduct')], null, '', '', true).then(ack => { });
  }

  public searchComponents(ids: string[]) {
    let parameters: string[] = [];
    ids.forEach(i => {
      parameters.push(`id=${encodeURIComponent(i)}`);
    });
    const api = `${this.apiEndpoint}/items/kitcomponentsbylist/?${parameters.join("&")}`;

    this.http.get<KitComponentsDtoResponse[]>(api).subscribe(componentsDto => {
      this.componentsSubject.next(componentsDto);
    }, Helper.handleError);
  }

  public isAggressiveFitmentFilterEnabled(route: ActivatedRoute): boolean {
    var isAggressive: boolean = false;
    route.queryParamMap.subscribe(params => {
      if (params.get('show') !== null) {
        const values = params.get('show').split(',').filter(value => value === 'Aggressive');
        if (values !== null)
          isAggressive = true;
      }
    });
    return isAggressive;
  }

  public isProductConcept(productDetails: ProductDetail[]) {
    var isConcept: boolean = false;
    if (productDetails.some(d => d.label === Constants.CONCEPT_DETAIL_LABEL)
      && productDetails.find(d => d.label === Constants.CONCEPT_DETAIL_LABEL).value.toLowerCase() === "true") {
      isConcept = true;
    }
    return isConcept;
  }
  public verifyIsProductValid(summary: PackageBuilderSummaryProduct) {
        if (summary != null && ((summary.productStatus != null && summary.productStatus != 'D') ||
      (summary.inventory != null && (summary.inventory.onHandQtyMtl > 0 || summary.inventory.onHandQtyCal > 0))))
      return true;
    else
      return false;
  }

  public isVehicleHaveTPMS() {
    const enRegex = RegExp('.+yes.+', 'gi');
    const frRegex = RegExp('.+oui.+', 'gi');
    if (this.packageBuilderOEMData.groups.some(g => g.items.some(i => i.itemName === 'Tpms'
      && (enRegex.test(i.itemValue) || enRegex.test(i.itemValueRear) || frRegex.test(i.itemValue) || frRegex.test(i.itemValueRear)))))
      return true;
    else
      return false;
  }
  //djb:diagnostics specific to this service
  private log(msg: string) {
    Helper.log(`PackageBuilderService[${this.id}].: ${msg} LoadedFromSession ${this.loadedFromSession}`);
  }

  setProductSelectorQty(prodChanged: Product) {
    this.prodSelectorQtyChanged.next(prodChanged);
  }

}
